import { Settings } from 'react-slick'
import { PropertyType } from './propertyType'
import { HOME_TYPES, HOME_TYPES_HOUSE } from 'src/constants/listings'
import { EveryStateOriginatingSystemNames } from 'src/constants/compliance/everyStateMlsComplianceData'

export interface CarouselBreakpoint {
  settings: Settings
  breakpoint: number
}

export interface CarouselListingSearchParams {
  tag: string
  maxPrice: number
  minPrice: number
  postalCode: string
}

export interface CarouselListing {
  id: string
  price: number
  bedrooms: number
  bathrooms: number
  mainPhoto: string
  pocketAmount?: number
  formattedPrice: string
  livingAreaSqFt: number
  mortgagePaymentDifference?: number
  searchParams: CarouselListingSearchParams
}

export const emptyCarouselListingSearchParamsFactory = (
  initialData?: Partial<CarouselListingSearchParams>
): CarouselListingSearchParams =>
  Object.assign(
    {
      tag: '',
      maxPrice: -1,
      minPrice: -1,
      postalCode: '',
    },
    initialData
  )

export const emptyCarouselListingFactory = (
  initialData?: Partial<CarouselListing>
): CarouselListing =>
  Object.assign(
    {
      id: '',
      price: -1,
      bedrooms: -1,
      bathrooms: -1,
      mainPhoto: '',
      pocketAmount: -1,
      formattedPrice: '',
      livingAreaSqFt: -1,
      mortgagePaymentDifference: -1,
      searchParams: emptyCarouselListingSearchParamsFactory(),
    },
    initialData
  )

export interface Coordinates {
  lon: number
  lat: number
}

export interface SearchListingAddress {
  id: number
  city: string
  label: string
  number: string
  street: string
  latitude: number
  longitude: number
  unitNumber: string
  postalCode: string
  stateOrProvince: string
}

export interface SearchListing {
  id: string
  vector: string
  status: string
  sqftTotal: number
  mlsNumber: string
  listingUrl: string
  priceCents: number
  bedroomsCount: number
  bathroomsCount: number
  priceFormatted: string
  rentPriceCents: number
  source: ListingSource
  coordinates: Coordinates
  propertyType: PropertyType
  rentPriceFormatted: string
  address: SearchListingAddress
  thumbnailImageFileNames: string[]
  originatingSystemName: EveryStateOriginatingSystemNames | null // will be null if source is ListHub
}

export interface ListingMarkersSearch {
  id: string
  lat: number
  lng: number
  priceCents: number
  listingUrl: string
  priceFormatted: string
  rentPriceFormatted: string
}

export interface ListingAddress {
  id: string
  city: string
  label: string
  number: string
  street: string
  latitude: number
  longitude: number
  unitNumber: string
  postalCode: string
  stateOrProvince: string
}

export interface ListingRoomsCounter {
  floorsCount: number
  bedroomsCount: number
  bathroomsCount: number
}

export interface ListingSqft {
  sqftLot: number
  sqftBuilding: number
}

export interface Listing {
  sqft: ListingSqft
  address: ListingAddress
  roomsCounter: ListingRoomsCounter
  thumbnailImageFileNames: string[]
}

export interface ListingHasAmenities {
  hasDeck: boolean
  hasPond: boolean
  hasPool: boolean
  hasPatio: boolean
  hasPorch: boolean
  hasGarden: boolean
  hasBasement: boolean
  hasSkylight: boolean
  hasFireplace: boolean
  hasHotTubSpa: boolean
  hasCeilingFan: boolean
  hasSecuritySystem: boolean
  hasVaultedCeiling: boolean
  hasSprinklerSystem: boolean
}

export interface ListingIs {
  isWaterfront: boolean
  isNewConstruction: boolean
}

export const LIST_HUB = 'ListHub'
export const EVERY_STATE = 'EveryState'
export const LISTING_SOURCES = [EVERY_STATE, LIST_HUB] as const
export type ListingSourceName = typeof LISTING_SOURCES[number]

export interface ListingSource {
  link: string
  name: ListingSourceName
}

export interface ListingDetailsListing extends Listing {
  id: string
  is: ListingIs
  has: ListingHasAmenities
  source: ListingSource
  status: string
  rooms: string[]
  loading: boolean
  mlsNumber: string
  createdAt: string
  updatedAt: string
  disclaimer: string
  providedBy: string
  priceCents: number
  utilities: string[]
  mlsNumbers: string[]
  finishings: unknown
  fullAddress: string
  description: string
  parkingTypes: unknown
  lastCheckedAt: string
  imageCoverUrl: string
  rentPriceCents: number
  priceCentsSqft: number
  updatedAtInWords: string
  yearBuilt: number | null
  architectureStyle: string
  propertyType: PropertyType
  utilitiesBuilding: unknown
  airConditioningInfo: unknown
  propertySubtype: PropertyType
  originatingSystemName: EveryStateOriginatingSystemNames | null
}

export interface VariousListings {
  similarHomes: CarouselListing[]
  listingsSearch: SearchListing[]
  listingsZipCode: CarouselListing[]
  listingsFavorites: ListingDetailsListing[]
  listingMarkersSearch: ListingMarkersSearch[]
  listingsEquitySmartSearch: CarouselListing[]
}

export interface ListingUiFlag {
  showSearchModal: boolean
}

export interface ListingLoading {
  similarHomes: false
  listingsSearch: false
  listingsFavorites: true
  listingMarkersSearch: false
  listingsEquitySmartSearch: false
}

export interface Listings {
  uiFlags: ListingUiFlag
  loading: ListingLoading
  listings: VariousListings
  listingsSource: string
  showYlopoModal: boolean
  searchListingsTotal: number
  listing: ListingDetailsListing
  similarListings: SearchListing[]
}

export const emptySearchAddressFactory = (
  initialData?: Partial<SearchListingAddress>
): SearchListingAddress =>
  Object.assign(
    {
      id: 0,
      city: '',
      label: '',
      street: '',
      number: '',
      latitude: 0,
      longitude: 0,
      unitNumber: '',
      postalCode: '',
      stateOrProvince: '',
    },
    initialData
  )

export const emptyCoordinatesFactory = (
  initialData?: Partial<Coordinates>
): Coordinates =>
  Object.assign(
    {
      lon: 0,
      lat: 0,
    },
    initialData
  )

export const emptySearchListingFactory = (
  initialData?: Partial<SearchListing>
): SearchListing =>
  Object.assign(
    {
      id: '',
      status: '',
      vector: '',
      sqftTotal: 0,
      mlsNumber: '',
      priceCents: 0,
      listingUrl: '',
      bedroomsCount: 0,
      bathroomsCount: 0,
      rentPriceCents: 0,
      source: 'ListHub',
      priceFormatted: '',
      rentPriceFormatted: '',
      propertyType: HOME_TYPES[0],
      originatingSystemName: null,
      thumbnailImageFileNames: [],
      address: emptySearchAddressFactory(),
      coordinates: emptyCoordinatesFactory(),
    },
    initialData
  )

export const emptyListingAddressFactory = (
  initialData?: Partial<ListingAddress>
): ListingAddress =>
  Object.assign(
    {
      id: '',
      city: '',
      label: '',
      number: '',
      street: '',
      latitude: -1,
      longitude: -1,
      unitNumber: '',
      postalCode: '',
      stateOrProvince: '',
    },
    initialData
  )

export const emptyListingRoomsCounterFactory = (
  initialData?: Partial<ListingRoomsCounter>
): ListingRoomsCounter =>
  Object.assign(
    {
      floorsCount: -1,
      bedroomsCount: -1,
      bathroomsCount: -1,
    },
    initialData
  )

export const emptyListingSqftFactory = (
  initialData?: Partial<ListingSqft>
): ListingSqft =>
  Object.assign(
    {
      sqftLot: -1,
      sqftBuilding: -1,
    },
    initialData
  )

export const emptyListingHasAmenitiesFactory = (
  initialData?: Partial<ListingHasAmenities>
): ListingHasAmenities =>
  Object.assign(
    {
      hasDeck: false,
      hasPond: false,
      hasPool: false,
      hasPatio: false,
      hasPorch: false,
      hasGarden: false,
      hasBasement: false,
      hasSkylight: false,
      hasFireplace: false,
      hasHotTubSpa: false,
      hasCeilingFan: false,
      hasSecuritySystem: false,
      hasVaultedCeiling: false,
      hasSprinklerSystem: false,
    },
    initialData
  )

export const emptyListingIsFactory = (
  initialData?: Partial<ListingIs>
): ListingIs =>
  Object.assign(
    {
      isWaterfront: false,
      isNewConstruction: false,
    },
    initialData
  )

export const emptyListingSourceFactory = (
  initialData?: Partial<ListingSource>
): ListingSource =>
  Object.assign(
    {
      name: EVERY_STATE,
      link: '',
    },
    initialData
  )

export const emptyListingDetailsListingFactory = (
  initialData?: Partial<ListingDetailsListing>
): ListingDetailsListing =>
  Object.assign(
    {
      id: '',
      rooms: [],
      status: '',
      yearBuilt: -1,
      createdAt: '',
      updatedAt: '',
      disclaimer: '',
      providedBy: '',
      utilities: [],
      mlsNumber: '',
      mlsNumbers: [],
      priceCents: -1,
      loading: false,
      fullAddress: '',
      description: '',
      finishings: null,
      imageCoverUrl: '',
      lastCheckedAt: '',
      parkingTypes: null,
      rentPriceCents: -1,
      priceCentsSqft: -1,
      updatedAtInWords: '',
      architectureStyle: '',
      utilitiesBuilding: null,
      airConditioningInfo: null,
      originatingSystemName: '',
      thumbnailImageFileNames: [],
      propertyType: HOME_TYPES_HOUSE,
      propertySubtype: HOME_TYPES_HOUSE,
      is: emptyListingIsFactory(),
      sqft: emptyListingSqftFactory(),
      source: emptyListingSourceFactory(),
      address: emptyListingAddressFactory(),
      has: emptyListingHasAmenitiesFactory(),
      roomsCounter: emptyListingRoomsCounterFactory(),
    },
    initialData
  )
